import { Product } from 'component-library/__product'
import { ImageWidth } from 'common/helpers/image'
import {
    getImageManagerWidths,
    get2xNearestImageWidth,
    AkamaiImageManagerWidth,
} from './imageManager'
import { ImageCrop } from 'common/client-data-types/image-item'

export interface ImageSrcInfo {
    product: Product
    initialWidth: ImageWidth
    srcCallback: (src: string, width: ImageWidth) => string
    ampRender?: boolean
}

// Threshold is just below the west listing card size, as we want those to resize and not just be 2x
const EXTENDED_SRC_SET_THRESHOLD = 250

/**
 * If the given initial width within src info is < EXTENDED_SRC_SET_THRESHOLD px
 * Then we just create a srcSet to support the nearest ImageWidth closest too the initialWidth * 2 as 2x,
 *
 * Otherwise we create source sets based on the ImageWidths
 * that are within akamai that are above 250 and below the images initial width (1x)
 */

export const getSrcSetValue = (srcInfo: ImageSrcInfo, crop: ImageCrop): string => {
    return srcInfo.initialWidth >= EXTENDED_SRC_SET_THRESHOLD
        ? generateSrcSetString(srcInfo, crop, false)
        : generateSrcSetString(srcInfo, crop, true)
}

export const generateSrcSetString = (
    srcInfo: ImageSrcInfo,
    crop: ImageCrop,
    retinaUpgrade: boolean
) => {
    const { product } = srcInfo
    const widths = getImageManagerWidths(srcInfo.product)

    if (!retinaUpgrade) {
        return widths
            .sort((a, b) => a - b)
            .filter(x => x >= EXTENDED_SRC_SET_THRESHOLD)
            .map(w => mapWidthsToSrcSetString(w, crop, srcInfo))
            .join(', ')
    }

    const nearest2xWidth = get2xNearestImageWidth(product, srcInfo.initialWidth)

    return nearest2xWidth
        ? `${srcInfo.srcCallback(crop.reference, nearest2xWidth as ImageWidth)} 2x`
        : ''
}

export function mapWidthsToSrcSetString(
    w: AkamaiImageManagerWidth,
    crop: ImageCrop,
    srcInfo: ImageSrcInfo
): string {
    return `${srcInfo.srcCallback(crop.reference, w as ImageWidth)} ${w}w`
}
